import React from 'react';
import { useNavigate } from 'react-router-dom';

interface AtRiskWidgetProps {
  data: any;
  config: Record<string, any>;
  loading: boolean;
  error: string | null;
  width: number;
  height: number;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);

const AtRiskWidget: React.FC<AtRiskWidgetProps> = ({ data, loading, error }) => {
  const navigate = useNavigate();
  const opportunities: any[] = Array.isArray(data) ? data : [];

  if (loading) {
    return (
      <Shell>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-8)' }}>
          <div style={{ width: '24px', height: '24px', border: '3px solid var(--border-light)', borderTopColor: 'var(--color-primary-600)', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>
          {error}
        </div>
      </Shell>
    );
  }

  if (opportunities.length === 0) {
    return (
      <Shell>
        <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--text-tertiary)' }}>
          <span style={{ fontSize: '32px', display: 'block', marginBottom: 'var(--space-2)' }}>✅</span>
          Nenhuma oportunidade em risco
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div style={{ overflow: 'auto', flex: 1 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
          <thead>
            <tr>
              <th style={thStyle}>Lead</th>
              <th style={thStyle}>Etapa</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Valor</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Dias</th>
            </tr>
          </thead>
          <tbody>
            {opportunities.map((opp: any) => (
              <tr
                key={opp.id}
                onClick={() => navigate('/deals')}
                style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-tertiary)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <td style={tdStyle}>
                  <div style={{ fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '160px' }}>
                    {opp.lead_name}
                  </div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                    {opp.internal_code}
                  </div>
                </td>
                <td style={tdStyle}>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                    {opp.stage_name}
                  </span>
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {formatCurrency(opp.total_value || 0)}
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: opp.days_inactive >= 14 ? 'rgba(239, 68, 68, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                    color: opp.days_inactive >= 14 ? '#ef4444' : '#f59e0b',
                    fontWeight: 600,
                    fontSize: 'var(--text-xs)',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    minWidth: '32px',
                  }}>
                    {opp.days_inactive}d
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Shell>
  );
};

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        padding: 'var(--space-4) var(--space-5)',
        borderBottom: '1px solid var(--border-light)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
      }}>
        <span style={{ fontSize: '16px' }}>⚠️</span>
        <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)', margin: 0 }}>
          Oportunidades em Risco
        </h3>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 'var(--space-2)' }}>
        {children}
      </div>
    </div>
  );
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: 'var(--space-2) var(--space-3)',
  fontSize: 'var(--text-xs)',
  fontWeight: 600,
  color: 'var(--text-tertiary)',
  borderBottom: '1px solid var(--border-light)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const tdStyle: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  borderBottom: '1px solid var(--glass-border)',
};

export default AtRiskWidget;
